import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppMode } from '../context/AppModeContext';
import { getOrganisations, getReportsByOrg } from '../api';

const STATUS_LABELS = { DRAFT: 'Draft', IN_PROGRESS: 'In Progress', REVIEW: 'Review', COMPLETED: 'Completed' };
const STATUS_COLORS = { DRAFT: '#6b7280', IN_PROGRESS: '#f59e0b', REVIEW: '#3b82f6', COMPLETED: '#10b981' };

export default function DataEntryPage() {
  const { user, logout } = useAuth();
  const { clearAppMode } = useAppMode();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOrganisations()
      .then((orgList) =>
        Promise.all(
          orgList.map((o) =>
            getReportsByOrg(o.id, { page: 0, size: 10000, sortBy: 'updatedAt', sortDir: 'desc' })
              .then((r) => r.data)
              .catch(() => [])
          )
        )
      )
      .then((results) => {
        const all = results.flat();
        const incomplete = all
          .filter((r) => r.assessorUserId === user.userId && r.status !== 'ASSESSOR_COMPLETED' && r.status !== 'QA_APPROVED')
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setReports(incomplete);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.userId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="data-entry-container">
      <header className="data-entry-header">
        <div className="data-entry-header-brand">
          <img src="/logo-pocketrams.png" alt="PocketRAMS" className="navbar-logo" />
        </div>
        <div className="data-entry-header-actions">
          <button className="btn btn-outline" onClick={clearAppMode}>Full Site</button>
          <button className="btn btn-outline" onClick={handleLogout} style={{ color: '#f87171', borderColor: '#f87171' }}>Logout</button>
        </div>
      </header>

      <div className="data-entry-body">
        <h1 className="data-entry-greeting">Hello, {user.firstName}</h1>

        <button className="btn btn-primary data-entry-new-btn" onClick={() => navigate('/reports/new')}>
          + New Report
        </button>

        <h2 className="data-entry-section-title">
          Your Incomplete Reports {!loading && `(${reports.length})`}
        </h2>

        {loading ? (
          <p className="data-entry-loading">Loading reports...</p>
        ) : reports.length === 0 ? (
          <p className="data-entry-empty">No incomplete reports found. Tap "New Report" to get started.</p>
        ) : (
          <div className="data-entry-report-list">
            {reports.map((r) => (
              <button key={r.id} className="data-entry-report-card" onClick={() => navigate(`/reports/${r.id}`)}>
                <div className="data-entry-report-card-top">
                  <span className="data-entry-report-site">{r.siteName || 'Unnamed site'}</span>
                  <span className="data-entry-status-badge" style={{ background: STATUS_COLORS[r.status] || '#6b7280' }}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </div>
                <div className="data-entry-report-card-meta">
                  <span>{r.organisationName}</span>
                  <span>{formatDate(r.assessmentDate)}</span>
                </div>
                <div className="data-entry-report-card-updated">
                  Last edited {formatDateTime(r.updatedAt)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
